import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaBullhorn,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaCalendarAlt,
  FaPaperPlane,
  FaArchive,
  FaUsers,
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheckCircle,
  FaBell,
  FaFilter,
  FaRedo,
  FaSearch,
  FaUserAlt,
  FaChevronLeft,
  FaChevronRight,
  FaImage,
  FaVideo,
  FaUpload
} from 'react-icons/fa';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import useConfirm from '../../hooks/useConfirm';
import { apiHelper } from '../../utils/api';
import { notify } from '../../utils/notifications';
import logger from '../../utils/logger';
import errorHandler from '../../utils/errorHandler';
import './Admin.scss';

// Theme options for announcements
const THEMES = [
  { value: 'default', label: 'Default', color: '#6366f1' },
  { value: 'info', label: 'Information', color: '#3b82f6' },
  { value: 'success', label: 'Success', color: '#10b981' },
  { value: 'warning', label: 'Warning', color: '#f59e0b' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' }
];

// Icon options
const ICON_OPTIONS = [
  { value: 'info', label: 'Info', icon: <FaInfoCircle /> },
  { value: 'announcement', label: 'Announcement', icon: <FaBullhorn /> },
  { value: 'warning', label: 'Warning', icon: <FaExclamationTriangle /> },
  { value: 'celebration', label: 'Celebration', icon: <FaCheckCircle /> },
  { value: 'update', label: 'Update', icon: <FaBell /> }
];

// Plan options
const PLAN_OPTIONS = [
  { value: 'all', label: 'All Plans' },
  { value: 'free', label: 'Free' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' }
];

// Default role options - will be replaced with dynamic roles
const DEFAULT_ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' }
];

// Repeat schedule options
const REPEAT_OPTIONS = [
  { value: 'once',          label: 'One-time',              hint: 'Show once per user. After dismissal it never shows again.' },
  { value: 'daily',         label: 'Every Day',             hint: 'Re-appears daily after the user dismisses it.' },
  { value: 'alternate_days',label: 'Alternate Days',        hint: 'Re-appears every 2 days after the user dismisses it.' },
  { value: 'weekly',        label: 'Every Week',            hint: 'Re-appears every 7 days after the user dismisses it.' },
  { value: 'every_10_days', label: 'Every 10 Days',         hint: 'Re-appears every 10 days after the user dismisses it.' },
  { value: 'every_15_days', label: 'Every 15 Days',         hint: 'Re-appears every 15 days after the user dismisses it.' },
  { value: 'monthly',       label: 'Monthly (30 days)',     hint: 'Re-appears every 30 days after the user dismisses it.' },
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    draft: { bg: '#6b7280', label: 'Draft' },
    scheduled: { bg: '#8b5cf6', label: 'Scheduled' },
    published: { bg: '#10b981', label: 'Published' },
    expired: { bg: '#ef4444', label: 'Expired' },
    archived: { bg: '#374151', label: 'Archived' }
  };

  const style = statusStyles[status] || statusStyles.draft;

  return (
    <span 
      className="announcement-status-badge"
      style={{ backgroundColor: style.bg }}
    >
      {style.label}
    </span>
  );
};

// Announcement form component
const AnnouncementForm = ({ announcement, onSave, onCancel, isLoading, roleOptions = DEFAULT_ROLE_OPTIONS }) => {
  const [form, setForm] = useState({
    title: '',
    content: '',
    mediaType: 'none',
    mediaUrl: '',
    targetPlans: ['all'],
    targetRoles: ['all'],
    targetUsers: [],
    publishAt: '',
    expiresAt: '',
    enablePublishAt: false,
    enableExpiresAt: false,
    status: 'draft',
    theme: 'default',
    repeatSchedule: 'once',
    customStyle: {
      backgroundColor: '',
      textColor: '',
      borderColor: '',
      iconType: 'announcement'
    },
    displayOptions: {
      dismissible: true,
      showAsModal: true,
      showAsBanner: true,
      priority: 0
    },
    sendNotification: {
      email: false,
      push: true,
      inApp: true
    }
  });

  const [targetedCount, setTargetedCount] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [contentMode, setContentMode] = useState('text'); // 'text' | 'media'
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // User search state
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isUserSearching, setIsUserSearching] = useState(false);
  const userSearchRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userSearchRef.current && !userSearchRef.current.contains(e.target)) {
        setUserSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced user search
  useEffect(() => {
    if (!userSearch || userSearch.length < 2) {
      setUserSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsUserSearching(true);
      try {
        const res = await apiHelper.get(`/admin/users?search=${encodeURIComponent(userSearch)}&limit=8`);
        setUserSearchResults(res.users || res.data || []);
      } catch (err) {
        logger.debug('User search failed', err, 'Announcements');
      } finally {
        setIsUserSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const addTargetUser = (user) => {
    setForm(prev => {
      if (prev.targetUsers.some(u => (u._id || u) === user._id)) return prev;
      return { ...prev, targetUsers: [...prev.targetUsers, { _id: user._id, name: user.name, email: user.email }] };
    });
    setUserSearch('');
    setUserSearchResults([]);
  };

  const removeTargetUser = (userId) => {
    setForm(prev => ({
      ...prev,
      targetUsers: prev.targetUsers.filter(u => (u._id || u) !== userId)
    }));
  };

  useEffect(() => {
    if (announcement) {
      setForm({
        ...form,
        ...announcement,
        mediaType: announcement.mediaType || 'none',
        mediaUrl: announcement.mediaUrl || '',
        repeatSchedule: announcement.repeatSchedule || 'once',
        // Normalize targetUsers to [{_id, name, email}] objects
        targetUsers: (announcement.targetUsers || []).map(u =>
          typeof u === 'object' && u._id ? { _id: u._id, name: u.name, email: u.email } : { _id: u }
        ),
        enablePublishAt: !!announcement.publishAt,
        enableExpiresAt: !!announcement.expiresAt,
        publishAt: announcement.publishAt
          ? (() => { const d = new Date(announcement.publishAt); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') + 'T' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0'); })()
          : '',
        expiresAt: announcement.expiresAt
          ? (() => { const d = new Date(announcement.expiresAt); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') + 'T' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0'); })()
          : ''
      });

      setContentMode(
        announcement.mediaType && announcement.mediaType !== 'none' ? 'media' : 'text'
      );
    }
  }, [announcement]);

  // Fetch targeted user count when targeting changes
  useEffect(() => {
    let cancelled = false;
    // If specific users are targeted, use that count directly
    if (form.targetUsers.length > 0) {
      setTargetedCount(form.targetUsers.length);
      return;
    }
    const fetchTargetedCount = async () => {
      try {
        const plans = form.targetPlans.join(',');
        const roles = form.targetRoles.join(',');
        const response = await apiHelper.get(`/announcements/segment-preview?plans=${plans}&roles=${roles}`);
        if (!cancelled) {
          setTargetedCount(response.data.count);
        }
      } catch (error) {
        if (!cancelled) {
          logger.debug('Error fetching targeted count', error, 'Announcements');
        }
      }
    };
    fetchTargetedCount();
    return () => {
      cancelled = true;
    };
  }, [form.targetPlans, form.targetRoles, form.targetUsers]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiHelper.upload('/uploads/announcement-media', formData);
      setForm(prev => ({
        ...prev,
        mediaUrl: result.url,
        mediaType: result.mediaType,
        // Media mode: discard textual content so the two are mutually exclusive
        content: '',
      }));
      setContentMode('media');
    } catch (err) {
      errorHandler.handleApiError(err, 'Announcements');
    } finally {
      setIsUploadingMedia(false);
      e.target.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setForm(prev => ({
      ...prev,
      mediaUrl: '',
      mediaType: 'none',
    }));
    setContentMode('text');
  };

  const handleNestedChange = (parent, field, value) => {
    setForm(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleMultiSelect = (field, value) => {
    setForm(prev => {
      const current = prev[field];
      if (value === 'all') {
        return { ...prev, [field]: ['all'] };
      }
      
      let newValues = current.filter(v => v !== 'all');
      if (newValues.includes(value)) {
        newValues = newValues.filter(v => v !== value);
      } else {
        newValues.push(value);
      }
      
      if (newValues.length === 0) {
        newValues = ['all'];
      }
      
      return { ...prev, [field]: newValues };
    });
  };

  // Helper: convert local YYYY-MM-DDTHH:mm to UTC ISO string
  function toISO(localStr) {
    if (!localStr) return null;
    const d = new Date(localStr);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalForm = {
      ...form,
      targetUsers: form.targetUsers.map(u => u._id || u),
      // Convert local datetimes to UTC ISO strings so timezone is preserved
      publishAt: form.enablePublishAt && form.publishAt ? toISO(form.publishAt) : null,
      expiresAt: form.enableExpiresAt && form.expiresAt ? toISO(form.expiresAt) : null,
    };
    onSave(finalForm);
  };

  const selectedTheme = THEMES.find(t => t.value === form.theme);

  return (
    <div className="announcement-form-overlay">
      <div className="announcement-form-modal">
        <div className="announcement-form-header">
          <h2>{announcement ? 'Edit Announcement' : 'Create Announcement'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <FaTimes />
          </button>
        </div>

        <div className="announcement-form-tabs">
          <button 
            className={`tab ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            Content
          </button>
          <button 
            className={`tab ${activeTab === 'targeting' ? 'active' : ''}`}
            onClick={() => setActiveTab('targeting')}
          >
            Targeting
          </button>
          <button 
            className={`tab ${activeTab === 'scheduling' ? 'active' : ''}`}
            onClick={() => setActiveTab('scheduling')}
          >
            Scheduling
          </button>
          <button 
            className={`tab ${activeTab === 'appearance' ? 'active' : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            Appearance
          </button>
        </div>

        <form onSubmit={handleSubmit} className="announcement-form-content">
          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="form-tab-content">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="Announcement title..."
                  maxLength={200}
                  required
                />
                <span className="char-count">{form.title.length}/200</span>
              </div>

              <div className="content-mode-toggle">
                <button
                  type="button"
                  className={`mode-btn ${contentMode === 'text' ? 'active' : ''}`}
                  onClick={() => setContentMode('text')}
                >
                  <FaBullhorn /> Text
                </button>
                <button
                  type="button"
                  className={`mode-btn ${contentMode === 'media' ? 'active' : ''}`}
                  onClick={() => setContentMode('media')}
                >
                  <FaImage /> Media
                </button>
              </div>

              {contentMode === 'text' ? (
                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={form.content}
                    onChange={(e) => handleChange('content', e.target.value)}
                    placeholder="Announcement content..."
                    rows={6}
                    maxLength={5000}
                    required
                  />
                  <span className="char-count">{form.content.length}/5000</span>
                </div>
              ) : (
                <div className="form-group">
                  <label>Media *</label>
                  <span className="hint">Upload an image or video (text content will be cleared)</span>

                  {form.mediaUrl ? (
                    <div className="announcement-media-preview">
                      {form.mediaType === 'video' ? (
                        <video src={form.mediaUrl} controls className="announcement-media-video" />
                      ) : (
                        <img src={form.mediaUrl} alt="Announcement media" className="announcement-media-img" />
                      )}
                      <button
                        type="button"
                        className="announcement-media-remove"
                        onClick={handleRemoveMedia}
                      >
                        <FaTimes /> Remove
                      </button>
                    </div>
                  ) : (
                    <div className="announcement-media-upload">
                      <input
                        type="file"
                        id="announcement-media-input"
                        accept="image/*,video/mp4,video/webm,video/ogg"
                        onChange={handleMediaUpload}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="announcement-media-input" className="announcement-media-label">
                        {isUploadingMedia ? (
                          <span className="announcement-media-uploading">Uploading...</span>
                        ) : (
                          <>
                            <FaUpload />
                            <span>Click to upload image or video</span>
                            <span className="hint">Supports: JPG, PNG, WebP, GIF, MP4, WebM</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Targeting Tab */}
          {activeTab === 'targeting' && (
            <div className="form-tab-content">
              <div className="targeting-preview">
                <FaUsers />
                <span>
                  This announcement will reach{' '}
                  <strong>{targetedCount ?? '...'}</strong>{' '}
                  {form.targetUsers.length > 0 ? 'specific user(s)' : 'users'}
                </span>
                {form.targetUsers.length > 0 && (
                  <span className="targeting-mode-badge">Specific Users Mode</span>
                )}
              </div>

              {/* Specific user targeting */}
              <div className="form-group">
                <label><FaUserAlt /> Target Specific Users</label>
                <span className="hint">When specific users are selected below, plan/role targeting is overridden.</span>

                <div className="user-search-wrap" ref={userSearchRef}>
                  <div className="user-search-input">
                    <FaSearch className="user-search-icon" />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                    />
                    {isUserSearching && <span className="user-search-spinner">&#8987;</span>}
                  </div>
                  {userSearchResults.length > 0 && (
                    <div className="user-search-dropdown">
                      {userSearchResults.map(user => (
                        <button
                          key={user._id}
                          type="button"
                          className="user-search-result"
                          onClick={() => addTargetUser(user)}
                        >
                          <span className="user-result-name">{user.name}</span>
                          <span className="user-result-email">{user.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {form.targetUsers.length > 0 && (
                  <div className="targeted-users-chips">
                    {form.targetUsers.map(user => (
                      <div key={user._id || user} className="user-chip">
                        <FaUserAlt className="user-chip-icon" />
                        <span>{user.name || user.email || String(user._id).slice(-6)}</span>
                        <button
                          type="button"
                          className="user-chip-remove"
                          onClick={() => removeTargetUser(user._id || user)}
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`targeting-section ${form.targetUsers.length > 0 ? 'targeting-section--muted' : ''}`}>
                <div className="targeting-section-label">
                  {form.targetUsers.length > 0 && <span className="targeting-disabled-note">&#9888; Plan/role targeting is ignored when specific users are selected</span>}
                </div>

                <div className="form-group">
                  <label>Target Plans</label>
                  <div className="chip-select">
                    {PLAN_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`chip ${form.targetPlans.includes(option.value) ? 'selected' : ''}`}
                        onClick={() => handleMultiSelect('targetPlans', option.value)}
                        disabled={form.targetUsers.length > 0}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Target Roles</label>
                  <div className="chip-select">
                    {roleOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        className={`chip ${form.targetRoles.includes(option.value) ? 'selected' : ''}`}
                        onClick={() => handleMultiSelect('targetRoles', option.value)}
                        disabled={form.targetUsers.length > 0}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label><FaBell /> Notification Channels</label>
                <span className="hint">Similar to event/project reminders — push works even when the app is closed (Web Push).</span>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.sendNotification.push}
                      onChange={(e) => handleNestedChange('sendNotification', 'push', e.target.checked)}
                    />
                    <span>Push notification <span style={{ color: '#10b981', fontWeight: 600 }}>(recommended)</span></span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.sendNotification.inApp}
                      onChange={(e) => handleNestedChange('sendNotification', 'inApp', e.target.checked)}
                    />
                    <span>In-app notification (bell + banner/modal)</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.sendNotification.email}
                      onChange={(e) => handleNestedChange('sendNotification', 'email', e.target.checked)}
                    />
                    <span>Email notification</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Display Options</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.displayOptions.dismissible}
                      onChange={(e) => handleNestedChange('displayOptions', 'dismissible', e.target.checked)}
                    />
                    <span>Allow users to dismiss</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.displayOptions.showAsBanner}
                      onChange={(e) => handleNestedChange('displayOptions', 'showAsBanner', e.target.checked)}
                    />
                    <span>Show as banner</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.displayOptions.showAsModal}
                      onChange={(e) => handleNestedChange('displayOptions', 'showAsModal', e.target.checked)}
                    />
                    <span>Show as modal popup</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Scheduling Tab */}
          {activeTab === 'scheduling' && (
            <div className="form-tab-content">
              <div className="form-group">
                <label>Status</label>
                <CustomSelect
                  value={form.status}
                  onChange={(val) => handleChange('status', val)}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'scheduled', label: 'Scheduled' },
                    { value: 'published', label: 'Published' },
                  ]}
                />
              </div>

              <div className="form-group">
                <label>
                  <FaRedo /> Repeat Schedule
                </label>
                <CustomSelect
                  value={form.repeatSchedule}
                  onChange={(val) => handleChange('repeatSchedule', val)}
                  options={REPEAT_OPTIONS.map(({ value, label }) => ({ value, label }))}
                />
                <span className="hint">
                  {REPEAT_OPTIONS.find(o => o.value === form.repeatSchedule)?.hint}
                </span>
              </div>

              {form.status === 'scheduled' && (
                <div className="form-group">
                  <label className="checkbox-label-inline">
                    <input
                      type="checkbox"
                      checked={form.enablePublishAt}
                      onChange={(e) => { handleChange('enablePublishAt', e.target.checked); if (!e.target.checked) handleChange('publishAt', ''); }}
                    />
                    <FaCalendarAlt /> Publish Date &amp; Time
                  </label>
                  {form.enablePublishAt && (
                    <>
                      <input
                        type="datetime-local"
                        value={form.publishAt}
                        onChange={(e) => handleChange('publishAt', e.target.value)}
                      />
                      <span className="hint">Announcement will be published at this date/time</span>
                    </>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label-inline">
                  <input
                    type="checkbox"
                    checked={form.enableExpiresAt}
                    onChange={(e) => { handleChange('enableExpiresAt', e.target.checked); if (!e.target.checked) handleChange('expiresAt', ''); }}
                  />
                  <FaCalendarAlt /> Expiration Date &amp; Time
                </label>
                {form.enableExpiresAt && (
                  <>
                    <input
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(e) => handleChange('expiresAt', e.target.value)}
                    />
                    <span className="hint">Announcement will expire and hide at this date/time</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="form-tab-content">
              <div className="form-group">
                <label>Theme</label>
                <div className="theme-select">
                  {THEMES.map(theme => (
                    <button
                      key={theme.value}
                      type="button"
                      className={`theme-option ${form.theme === theme.value ? 'selected' : ''}`}
                      style={{ 
                        borderColor: form.theme === theme.value ? theme.color : 'transparent',
                        backgroundColor: form.theme === theme.value ? `${theme.color}15` : 'transparent'
                      }}
                      onClick={() => handleChange('theme', theme.value)}
                    >
                      <span className="theme-dot" style={{ backgroundColor: theme.color }}></span>
                      {theme.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Icon</label>
                <div className="icon-select">
                  {ICON_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={`icon-option ${form.customStyle.iconType === option.value ? 'selected' : ''}`}
                      style={{ 
                        color: selectedTheme?.color,
                        borderColor: form.customStyle.iconType === option.value ? selectedTheme?.color : undefined,
                        backgroundColor: form.customStyle.iconType === option.value ? `${selectedTheme?.color}15` : 'transparent'
                      }}
                      onClick={() => handleNestedChange('customStyle', 'iconType', option.value)}
                    >
                      {option.icon || 'None'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="appearance-preview">
                <h4>Preview</h4>
                <div 
                  className={`announcement-preview theme-${form.theme}`}
                  style={{
                    backgroundColor: form.customStyle.backgroundColor || undefined,
                    color: form.customStyle.textColor || undefined,
                    borderColor: form.customStyle.borderColor || selectedTheme?.color
                  }}
                >
                  <div className="preview-icon" style={{ color: selectedTheme?.color }}>
                    {ICON_OPTIONS.find(i => i.value === form.customStyle.iconType)?.icon || <FaBullhorn />}
                  </div>
                  <div className="preview-content">
                    <strong>{form.title || 'Announcement Title'}</strong>
                    <p>{form.content || 'Announcement content will appear here...'}</p>
                  </div>
                  {form.displayOptions.dismissible && (
                    <button className="preview-dismiss">×</button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Custom Colors (Optional)</label>
                <div className="color-inputs">
                  <div className="color-input">
                    <label>Background</label>
                    <input
                      type="color"
                      value={form.customStyle.backgroundColor || '#ffffff'}
                      onChange={(e) => handleNestedChange('customStyle', 'backgroundColor', e.target.value)}
                    />
                  </div>
                  <div className="color-input">
                    <label>Text</label>
                    <input
                      type="color"
                      value={form.customStyle.textColor || '#000000'}
                      onChange={(e) => handleNestedChange('customStyle', 'textColor', e.target.value)}
                    />
                  </div>
                  <div className="color-input">
                    <label>Border</label>
                    <input
                      type="color"
                      value={form.customStyle.borderColor || '#6366f1'}
                      onChange={(e) => handleNestedChange('customStyle', 'borderColor', e.target.value)}
                    />
                  </div>
                  <button 
                    type="button" 
                    className="reset-colors-btn"
                    onClick={() => handleChange('customStyle', {
                      ...form.customStyle,
                      backgroundColor: '',
                      textColor: '',
                      borderColor: ''
                    })}
                  >
                    Reset Colors
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="announcement-form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (announcement ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Announcements component
const Announcements = () => {
  const confirm = useConfirm();
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Fetch user roles/designations for targeting
  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiHelper.get('/admin/users/designations');
      const designations = response.designations || [];
      // Convert to role options format
      const options = [
        { value: 'all', label: 'All Roles' },
        ...designations.map(d => ({
          value: d.toLowerCase(),
          label: d
        }))
      ];
      setRoleOptions(options);
    } catch (error) {
      logger.debug('Error fetching roles', error, 'Announcements');
    }
  }, []);

  // Fetch announcements
  const fetchAnnouncements = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        page: pagination.page,
        limit: pagination.limit,
      });
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      const response = await apiHelper.get(`/announcements?${params.toString()}`);
      setAnnouncements(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      errorHandler.handleApiError(error, 'Announcements');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchTerm, pagination.page, pagination.limit]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiHelper.get('/announcements/stats');
      setStats(response.data);
    } catch (error) {
      logger.debug('Error fetching stats', error, 'Announcements');
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    fetchStats();
    fetchRoles();
  }, [fetchAnnouncements, fetchStats, fetchRoles]);

  // Handle save
  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      if (editingAnnouncement) {
        await apiHelper.put(`/announcements/${editingAnnouncement._id}`, formData);
        notify.success('Announcement updated successfully');
      } else {
        await apiHelper.post('/announcements', formData);
        notify.success('Announcement created successfully');
      }
      setIsFormOpen(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
      fetchStats();
    } catch (error) {
      errorHandler.handleApiError(error, 'Announcements');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    const confirmed = await confirm('Are you sure you want to delete this announcement?', {
      title: 'Delete Announcement',
      confirmText: 'Delete',
      showWarning: true,
    });
    if (!confirmed) {
      return;
    }

    try {
      await apiHelper.delete(`/announcements/${id}`);
      notify.success('Announcement deleted');
      fetchAnnouncements();
      fetchStats();
    } catch (error) {
      errorHandler.handleApiError(error, 'Announcements');
    }
  };

  // Handle publish
  const handlePublish = async (id) => {
    try {
      await apiHelper.post(`/announcements/${id}/publish`);
      notify.success('Announcement published');
      fetchAnnouncements();
      fetchStats();
    } catch (error) {
      errorHandler.handleApiError(error, 'Announcements');
    }
  };

  // Handle archive
  const handleArchive = async (id) => {
    try {
      await apiHelper.post(`/announcements/${id}/archive`);
      notify.success('Announcement archived');
      fetchAnnouncements();
      fetchStats();
    } catch (error) {
      errorHandler.handleApiError(error, 'Announcements');
    }
  };

  // Open edit form
  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setIsFormOpen(true);
  };

  // Open create form
  const handleCreate = () => {
    setEditingAnnouncement(null);
    setIsFormOpen(true);
  };

  // Get stat count
  const getStatCount = (status) => {
    if (!stats?.byStatus) return 0;
    const stat = stats.byStatus.find(s => s._id === status);
    return stat?.count || 0;
  };

  return (
    <div className="admin-announcements">
      {/* Header */}
      <div className="announcements-header">
        <div className="header-left">
          <h2>
            <FaBullhorn /> Announcements
          </h2>
          <p>Create and manage announcements for your users</p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <FaPlus /> New Announcement
        </button>
      </div>

      {/* Stats */}
      <div className="announcements-stats">
        <div
          className={`stat-card${statusFilter === 'draft' ? ' active' : ''}`}
          onClick={() => setStatusFilter(prev => prev === 'draft' ? 'all' : 'draft')}
        >
          <span className="stat-value">{getStatCount('draft')}</span>
          <span className="stat-label">Drafts</span>
        </div>
        <div
          className={`stat-card${statusFilter === 'scheduled' ? ' active' : ''}`}
          onClick={() => setStatusFilter(prev => prev === 'scheduled' ? 'all' : 'scheduled')}
        >
          <span className="stat-value">{getStatCount('scheduled')}</span>
          <span className="stat-label">Scheduled</span>
        </div>
        <div
          className={`stat-card published${statusFilter === 'published' ? ' active' : ''}`}
          onClick={() => setStatusFilter(prev => prev === 'published' ? 'all' : 'published')}
        >
          <span className="stat-value">{getStatCount('published')}</span>
          <span className="stat-label">Published</span>
        </div>
        <div
          className={`stat-card${statusFilter === 'all' || statusFilter === '' ? ' active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <span className="stat-value">{getStatCount('draft') + getStatCount('scheduled') + getStatCount('published')}</span>
          <span className="stat-label">Total Announcements</span>
        </div>
      </div>

      {/* Filters */}
      <div className="announcements-filters">
        <div className="filter-group">
          <FaFilter />
          <CustomSelect
            value={statusFilter}
            onChange={(val) => { setStatusFilter(val); setPagination(prev => ({ ...prev, page: 1 })); }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'draft', label: 'Drafts' },
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'published', label: 'Published' },
              { value: 'expired', label: 'Expired' },
              { value: 'archived', label: 'Archived' },
            ]}
          />
        </div>
        <div className="filter-group" style={{ marginLeft: 'auto', minWidth: 200 }}>
          <FaSearch />
          <input
            type="text"
            placeholder="Search by title or content..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
          />
          {searchTerm && (
            <button className="admin-search__clear" onClick={() => { setSearchTerm(''); setPagination(prev => ({ ...prev, page: 1 })); }}>
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* Announcements List */}
      <div className="announcements-list">
        {isLoading ? (
          <div className="loading-state">Loading announcements...</div>
        ) : announcements.length === 0 ? (
          <div className="empty-state">
            <FaBullhorn />
            <h3>No announcements found</h3>
            <p>Create your first announcement to reach your users</p>
            <button className="btn-primary" onClick={handleCreate}>
              <FaPlus /> Create Announcement
            </button>
          </div>
        ) : (
          announcements.map(announcement => (
            <div key={announcement._id} className={`announcement-card theme-${announcement.theme}`}>
              <div className={`announcement-card-inner ${announcement.mediaType !== 'none' && announcement.mediaUrl ? 'has-media' : ''}`}>
                <div className="announcement-card-info">
                  <div className="announcement-card-header">
                    <h3>
                    {announcement.title}
                    {announcement.mediaType === 'image' && <FaImage className="announcement-card-media-icon" title="Has image" />}
                    {announcement.mediaType === 'video' && <FaVideo className="announcement-card-media-icon" title="Has video" />}
                  </h3>
                    <StatusBadge status={announcement.status} />
                  </div>
                  {(!announcement.mediaType || announcement.mediaType === 'none') && (
                    <p className="announcement-content">{announcement.content}</p>
                  )}
                  <div className="announcement-meta">
                    {announcement.targetUsers?.length > 0 ? (
                      <span className="meta-specific-users">
                        <FaUserAlt /> {announcement.targetUsers.length} specific user{announcement.targetUsers.length !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span>
                        <FaUsers /> {announcement.targetPlans.includes('all') ? 'All plans' : announcement.targetPlans.join(', ')}
                      </span>
                    )}
                    <span>
                      <FaEye /> {announcement.viewCount} views
                    </span>
                    <span>
                      <FaRedo /> {REPEAT_OPTIONS.find(o => o.value === announcement.repeatSchedule)?.label || 'One-time'}
                    </span>
                    {announcement.publishAt && (
                      <span>
                        <FaCalendarAlt /> Publishes: {new Date(announcement.publishAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(announcement.publishAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {announcement.expiresAt && (
                      <span style={{ color: '#ef4444' }}>
                        <FaCalendarAlt /> Expires: {new Date(announcement.expiresAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(announcement.expiresAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <div className="announcement-actions">
                    <button onClick={() => handleEdit(announcement)} title="Edit">
                      <FaEdit />
                    </button>
                    {announcement.status === 'draft' && (
                      <button onClick={() => handlePublish(announcement._id)} title="Publish">
                        <FaPaperPlane />
                      </button>
                    )}
                    {announcement.status === 'published' && (
                      <button onClick={() => handleArchive(announcement._id)} title="Archive">
                        <FaArchive />
                      </button>
                    )}
                    <button 
                      onClick={() => handleDelete(announcement._id)} 
                      className="delete-btn"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                {announcement.mediaType !== 'none' && announcement.mediaUrl && (
                  <div className="announcement-card-media">
                    {announcement.mediaType === 'video' ? (
                      <video src={announcement.mediaUrl} controls className="announcement-card-media-asset" />
                    ) : (
                      <img src={announcement.mediaUrl} alt="" className="announcement-card-media-asset" />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="admin-pagination" style={{ marginTop: 24 }}>
          <div className="admin-pagination__info">
            <span>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </span>
          </div>
          <div className="admin-pagination__controls">
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || isLoading}
              onClick={() => setPagination(prev => ({ ...prev, page: 1 }))}
            >
              First
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === 1 || isLoading}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <FaChevronLeft /> Previous
            </button>
            <span className="admin-pagination__page-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || isLoading}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next <FaChevronRight />
            </button>
            <button
              className="admin-pagination__btn"
              disabled={pagination.page === pagination.pages || isLoading}
              onClick={() => setPagination(prev => ({ ...prev, page: pagination.pages }))}
            >
              Last
            </button>
          </div>
          <div className="admin-pagination__size">
            <label>Per page:</label>
            <CustomSelect
              value={pagination.limit}
              onChange={(newLimit) => setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))}
              options={[
                { value: 10, label: '10' },
                { value: 20, label: '20' },
                { value: 50, label: '50' },
              ]}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <AnnouncementForm
          announcement={editingAnnouncement}
          onSave={handleSave}
          onCancel={() => {
            setIsFormOpen(false);
            setEditingAnnouncement(null);
          }}
          isLoading={isSaving}
          roleOptions={roleOptions}
        />
      )}
    </div>
  );
};

export default Announcements;
